import React, { useLayoutEffect, useRef, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import theme from '../../../theme'
import { black, grey } from '../../../colors'
import CloseIcon from '@mui/icons-material/Close'
import GenericButton from '../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../components/GenericButton/styles'
import { NavigationAndPageCountContainer, StyledBoxedOffComponent, StyledHighlightBox, StyledHighlightBoxContainer, StyledTextBoxComponent, titleStyle } from './styles'

interface HighlightBoxProps {
  left?: string | undefined
  height?: string
  top?: string | undefined
  handleClose?: () => void
  handleNextClicked?: () => void
  title?: string
  description?: string
  width?: string
  disableHighlight?: boolean
  index: number
  stepLength: number
  textBoxXPlacement?: string
  textBoxYPlacement?: string
  handlePreviousClicked: () => void
}

const convertRemToPixels = (rem: number): number => {
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize)
}

export const HighlightBox: React.FC<HighlightBoxProps> = ({
  left,
  top,
  height,
  handleClose,
  handleNextClicked,
  title,
  description,
  width,
  disableHighlight = false,
  index,
  stepLength,
  textBoxXPlacement = 'start',
  textBoxYPlacement = 'bottom',
  handlePreviousClicked
}): JSX.Element => {
  const [textBoxYPlacementCalculation, setTextBoxYPlacementCalculation] = useState<string>('0')
  const textBoxRef = useRef<HTMLDivElement>(null)
  const paddingValue = convertRemToPixels(parseFloat(theme.spacing(2)))
  const paddedHeight = height !== 'auto' ? `${parseFloat(height ?? '0') + paddingValue}px` : 'auto'
  const paddedWidth = width !== 'auto' ? `${parseFloat(width ?? '0') + paddingValue}px` : 'auto'
  const shiftedHeight = height ? -1 * paddingValue / 2 : 0
  const shiftedWidth = width ? -1 * paddingValue / 2 : 0

  const textBoxXPlacementCalculation =
    textBoxXPlacement === 'end' && width
      ? `calc(${width} - ${theme.spacing(100)})`
      : 0

  useLayoutEffect(() => {
    if (textBoxRef.current && height && textBoxYPlacement === 'top') {
      const measuredHeight = textBoxRef.current.offsetHeight
      const spacing = convertRemToPixels(parseInt(theme.spacing(4)))
      setTextBoxYPlacementCalculation(`calc(-${measuredHeight}px - ${height} - ${spacing}px)`)
    } else {
      setTextBoxYPlacementCalculation('0')
    }
  }, [description, height])

  return (
    <StyledHighlightBoxContainer left={left} top={top} width={paddedWidth} height={paddedHeight}>
      <StyledHighlightBox left={shiftedWidth} top={shiftedHeight}>
        {/* Boxed-Off Component */}
        <StyledBoxedOffComponent theme={theme} disableHighlight={disableHighlight} />

        {/* Text Box Component */}
        <StyledTextBoxComponent theme={theme} ml={textBoxXPlacementCalculation} mt={textBoxYPlacementCalculation} ref={textBoxRef}>

          {/* Header and Close Icon */}
          <Box sx={titleStyle(theme)}>
            <Typography>{title}</Typography>
            <CloseIcon onClick={handleClose} sx={{ cursor: 'pointer' }} />
          </Box>

          {/* Description Box */}
          <Box sx={{ p: theme.spacing(6), textAlign: 'flex-start' }}>{description}</Box>

          {/* Navigation Buttons And Page Count */}
          <NavigationAndPageCountContainer theme={theme}>
            <Typography>{index >= 1 && `${index}/${stepLength}`}</Typography>

            <Stack direction='row' spacing={2}>
              <GenericButton
                label= {index === 0 ? 'Leave' : '< Previous' }
                variant='contained'
                buttonType={ButtonType.Ghost}
                buttonSize={ButtonSize.Small}
                onClick={handlePreviousClicked}
              />
              <GenericButton
                label= { index === stepLength ? 'Finish' : 'Next >' }
                variant='contained'
                buttonType={ButtonType.Primary}
                buttonSize={ButtonSize.Small}
                onClick={handleNextClicked}
              />
            </Stack>
          </NavigationAndPageCountContainer>
        </StyledTextBoxComponent>
      </StyledHighlightBox>
    </StyledHighlightBoxContainer>
  )
}
